import React, { useState, useEffect } from "react";
import api from "../../../../api";
import {
 Card,
 Row,
 Col,
 Table,
 Badge,
 ProgressBar,
 Modal,
 Button,
} from "react-bootstrap";
import {
 PieChart,
 Pie,
 Cell,
 Tooltip,
 Legend,
 BarChart,
 Bar,
 XAxis,
 YAxis,
 CartesianGrid,
} from "recharts";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import usePermission from "../../../../hooks/usePermission";
import VerifiedCopyActions from "../../Common/VerifiedCopyActions";

const COLORS = {
 draft: "#6C757D",
 submitted: "#FFC107",
 verified: "#0D6EFD",
 invoiced: "#17A2B8",
 paid: "#28A745",
 overdue: "#DC3545",
};

const BillingTab = () => {
 const { id: workOrderId } = useParams();
 const { role } = usePermission();
 const isClientUser = role === "client" || role === "po";

 const [billingList, setBillingList] = useState([]);
 const [statusData, setStatusData] = useState([]);
 const [pendingPayments, setPendingPayments] = useState([]);
 const [budgetSummary, setBudgetSummary] = useState([]);
 const [loading, setLoading] = useState(true);

 const [selectedBilling, setSelectedBilling] = useState(null);
 const [showModal, setShowModal] = useState(false);
 const [previewingPdf, setPreviewingPdf] = useState(false);
 const [downloadingPdf, setDownloadingPdf] = useState(false);

 useEffect(() => {
 fetchBilling();
 }, [workOrderId]);

 const fetchBilling = async () => {
 try {
 setLoading(true);
 const res = await api.get("/billing", { params: { workOrder: workOrderId } });

 if (res.data.success) {
 const bills = res.data.data || [];
 setBillingList(bills);

 // STATUS PIE CHART
 const statusMap = bills.reduce((acc, b) => {
 acc[b.status || "Unknown"] = (acc[b.status || "Unknown"] || 0) + 1;
 return acc;
 }, {});

 setStatusData(
 Object.entries(statusMap).map(([name, value]) => ({
 name,
 value,
 fill: COLORS[name.toLowerCase()] || "#8884d8",
 }))
 );

 // WORK ORDER BUDGET
 const woMap = {};
 bills.forEach((b) => {
 const w = b.workOrder;
 if (!w) return;

 if (!woMap[w._id]) {
 woMap[w._id] = {
 title: w.title || w.woNumber || "Work Order",
 totalBudget: w.totalBudget || 0,
 used: 0,
 };
 }
 woMap[w._id].used += Number(b.totalAmount || 0);
 });

 setBudgetSummary(Object.values(woMap));

 // PENDING PAYMENTS CHART
 setPendingPayments(
 bills
 .filter((b) => b.amountDue > 0)
 .map((b) => ({
 title: b.invoiceNumber,
 value: b.amountDue,
 }))
 );
 }
 } catch (err) {
 console.error("Billing fetch error:", err);
 } finally {
 setLoading(false);
 }
 };

 const formatCurrency = (val) =>
 `₹ ${Number(val || 0).toLocaleString("en-IN", {
 minimumFractionDigits: 2,
 })}`;

 const formatDate = (d) =>
 d
 ? new Date(d).toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 })
 : "—";

 const getStatusBadge = (status) => {
 switch (status?.toLowerCase()) {
 case "paid": return "success";
 case "submitted": return "warning";
 case "overdue": return "danger";
 case "verified": return "primary";
 case "invoiced": return "info";
 default: return "secondary";
 }
 };

 // ==============================
 // PDF ACTIONS
 // ==============================
 const readBlobText = async (blob) => {
 if (!(blob instanceof Blob)) return "";
 if (typeof blob.text === "function") return blob.text();

 return new Promise((resolve) => {
 const reader = new FileReader();
 reader.onload = () => resolve(String(reader.result || ""));
 reader.onerror = () => resolve("");
 reader.readAsText(blob);
 });
 };

 const handlePreviewPDF = async (id) => {
 const previewWindow = window.open("", "_blank");
 try {
 setPreviewingPdf(true);
 if (previewWindow) {
 previewWindow.document.write("<p>Loading PDF preview...</p>");
 }

 const signedRes = await api.get(`/billing/${id}/pdf`, {
 params: { mode: "preview", signed: "1" },
 timeout: 30000,
 suppressErrorToast: true,
 });

 if (signedRes.data?.cached && signedRes.data?.url) {
 console.info("[billing-pdf] Preview served from S3 signed URL", {
 billingId: id,
 mode: signedRes.data.mode,
 fileName: signedRes.data.fileName,
 });

 if (previewWindow) {
 previewWindow.location.href = signedRes.data.url;
 } else {
 window.open(signedRes.data.url, "_blank");
 }
 return;
 }

 console.info("[billing-pdf] Preview S3 cache miss, falling back to backend PDF generation", {
 billingId: id,
 mode: signedRes.data?.mode,
 });

 const res = await api.get(`/billing/${id}/pdf`, {
 params: { mode: "preview" },
 responseType: "blob",
 timeout: 900000,
 suppressErrorToast: true,
 });

 const contentType = res.headers?.["content-type"] || "";
 if (!contentType.includes("application/pdf")) {
 if (previewWindow) previewWindow.close();
 const errorText = await readBlobText(res.data);
 Swal.fire(
 "PDF Generation Failed",
 errorText ? `Server error: ${errorText.slice(0, 300)}` : "The server could not generate the PDF.",
 "error"
 );
 return;
 }

 const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
 if (previewWindow) {
 previewWindow.location.href = url;
 } else {
 const a = document.createElement("a");
 a.href = url;
 a.download = `INVOICE-${id}.pdf`;
 document.body.appendChild(a);
 a.click();
 document.body.removeChild(a);
 }
 window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
 } catch (err) {
 if (previewWindow) previewWindow.close();
 let errMsg = err.message || "Failed to preview Invoice PDF";
 if (err.response?.data instanceof Blob) {
 const text = await readBlobText(err.response.data);
 if (text) errMsg = text;
 }
 Swal.fire("Error", errMsg.slice(0, 300), "error");
 } finally {
 setPreviewingPdf(false);
 }
 };

 const handleDownloadPDF = async (id) => {
 try {
 setDownloadingPdf(true);

 const signedRes = await api.get(`/billing/${id}/pdf`, {
 params: { signed: "1" },
 timeout: 30000,
 });

 if (signedRes.data?.cached && signedRes.data?.url) {
 console.info("[billing-pdf] Served from S3 signed URL", {
 billingId: id,
 mode: signedRes.data.mode,
 fileName: signedRes.data.fileName,
 });

 const a = document.createElement("a");
 a.href = signedRes.data.url;
 a.download = signedRes.data.fileName || `INVOICE-${id}.pdf`;
 document.body.appendChild(a);
 a.click();
 document.body.removeChild(a);

 if (signedRes.data.mode === "download-lite") {
 try {
 const excelRes = await api.get(`/billing/${id}/excel`, {
 responseType: "blob",
 timeout: 900000,
 });

 const excelUrl = window.URL.createObjectURL(
 new Blob([excelRes.data], {
 type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 }),
 );
 const excelLink = document.createElement("a");
 excelLink.href = excelUrl;
 excelLink.download = `INVOICE-${id}-FULL-DATA.xlsx`;
 document.body.appendChild(excelLink);
 excelLink.click();
 document.body.removeChild(excelLink);
 window.setTimeout(() => window.URL.revokeObjectURL(excelUrl), 3000);

 Swal.fire(
 "Large Invoice Exported",
 "A lighter PDF was downloaded, and the full billing data was also downloaded in Excel.",
 "info",
 );
 } catch (excelError) {
 console.error("Full data Excel download error:", excelError);
 Swal.fire(
 "PDF Downloaded",
 "The invoice PDF was very large, so a lighter PDF was downloaded. Full data Excel could not be downloaded automatically.",
 "warning",
 );
 }
 }

 return;
 }

 console.info("[billing-pdf] S3 cache miss, falling back to backend PDF generation", {
 billingId: id,
 mode: signedRes.data?.mode,
 });

 const res = await api.get(`/billing/${id}/pdf`, {
 responseType: "blob",
 timeout: 900000,
 });
 const url = window.URL.createObjectURL(
 new Blob([res.data], { type: "application/pdf" }),
 );
 const a = document.createElement("a");
 a.href = url;
 a.download = `INVOICE-${id}.pdf`;
 document.body.appendChild(a);
 a.click();
 document.body.removeChild(a);
 window.setTimeout(() => window.URL.revokeObjectURL(url), 3000);

 if (res.headers["x-billing-pdf-mode"] === "download-lite") {
 try {
 const excelRes = await api.get(`/billing/${id}/excel`, {
 responseType: "blob",
 timeout: 900000,
 });

 const excelUrl = window.URL.createObjectURL(
 new Blob([excelRes.data], {
 type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
 }),
 );
 const excelLink = document.createElement("a");
 excelLink.href = excelUrl;
 excelLink.download = `INVOICE-${id}-FULL-DATA.xlsx`;
 document.body.appendChild(excelLink);
 excelLink.click();
 document.body.removeChild(excelLink);
 window.setTimeout(() => window.URL.revokeObjectURL(excelUrl), 3000);

 Swal.fire(
 "Large Invoice Exported",
 "A lighter PDF was downloaded, and the full billing data was also downloaded in Excel.",
 "info",
 );
 } catch (excelError) {
 console.error("Full data Excel download error:", excelError);
 Swal.fire(
 "PDF Downloaded",
 "The invoice PDF was very large, so a lighter PDF was downloaded. Full data Excel could not be downloaded automatically.",
 "warning",
 );
 }
 }
 } catch (err) {
 console.error("Invoice PDF download error:", err);
 Swal.fire("Error", "Failed to download Invoice PDF", "error");
 } finally {
 setDownloadingPdf(false);
 }
 };

 const handleView = async (id) => {
 try {
 const res = await api.get(`/billing/${id}`);
 if (res.data.success) {
 setSelectedBilling(res.data.data);
 setShowModal(true);
 }
 } catch (err) {
 Swal.fire("Error", "Failed to load billing details", "error");
 }
 };

 if (loading) return <p>Loading billing...</p>;

 return (
 <>
 <Row className="g-4">
 {/* STATUS PIE */}
 <Col md={4}>
 <Card className="shadow-sm border-0 p-3">
 <h6 className="fw-bold mb-3">Billing Status Distribution</h6>
 <PieChart width={260} height={260}>
 <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={90} label>
 {statusData.map((d, i) => (
 <Cell key={i} fill={d.fill} />
 ))}
 </Pie>
 <Tooltip />
 <Legend />
 </PieChart>
 </Card>
 </Col>

 {/* WORK ORDER BUDGET */}
 <Col md={4}>
 <Card className="shadow-sm border-0 p-3">
 <h6 className="fw-bold mb-3">Work Order Budget Usage</h6>
 {budgetSummary.map((wo, i) => {
 const pct = wo.totalBudget ? (wo.used / wo.totalBudget) * 100 : 0;
 return (
 <div key={i} className="mb-4">
 <div className="d-flex justify-content-between">
 <strong>{wo.title}</strong>
 <span>{pct.toFixed(0)}%</span>
 </div>
 <ProgressBar
 now={pct}
 variant={pct > 90 ? "danger" : pct > 60 ? "warning" : "success"}
 />
 <small className="text-muted d-block mt-1">
 {formatCurrency(wo.used)} / {formatCurrency(wo.totalBudget)}
 </small>
 </div>
 );
 })}
 </Card>
 </Col>

 {/* PENDING CHART */}
 <Col md={4}>
 <Card className="shadow-sm border-0 p-3">
 <h6 className="fw-bold mb-3">{isClientUser ? "Pending Payment Release" : "Pending Payments"}</h6>

 {pendingPayments.length === 0 ? (
 <p className="text-muted">No pending invoices</p>
 ) : (
 <BarChart width={300} height={240} data={pendingPayments}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis dataKey="title" />
 <YAxis />
 <Tooltip formatter={(v) => formatCurrency(v)} />
 <Bar dataKey="value" fill={COLORS.overdue} />
 </BarChart>
 )}
 </Card>
 </Col>

 {/* INVOICE TABLE */}
 <Col md={12}>
 <Card className="shadow-sm border-0 p-3">
 <h5 className="fw-bold mb-3">Invoices</h5>
 <Table hover responsive>
 <thead>
 <tr>
 <th>#</th>
 <th>Invoice</th>
 <th>Status</th>
 <th>Total</th>
 <th>Due</th>
 <th>Date</th>
 <th>PDF</th>
 <th>View</th>
 </tr>
 </thead>
 <tbody>
 {billingList.map((b, i) => (
 <tr key={b._id}>
 <td>{i + 1}</td>
 <td>{b.invoiceNumber}</td>
 <td>
 <Badge
 className="rounded-pill"
 bg={getStatusBadge(b.status)}
 >
 {b.status}
 </Badge>
 </td>
 <td>{formatCurrency(b.totalAmount)}</td>
 <td className={b.amountDue > 0 ? "text-danger" : "text-success"}>
 {formatCurrency(b.amountDue)}
 </td>
 <td>{formatDate(b.createdAt)}</td>
 <td>
 <VerifiedCopyActions 
 id={b._id} 
 type="billing" 
 hasVerifiedCopy={
 !!(b.verifiedCopy?.filePath || b.verifiedCopy?.fileUrl)
 } 
 verifiedCopy={b.verifiedCopy}
 onUploadSuccess={fetchBilling}
 />
 </td>
 <td>
 <button
 className="btn btn-sm btn-outline-primary"
 onClick={() => handleView(b._id)}
 >
 <i className="fas fa-eye"></i>
 </button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </Card>
 </Col>
 </Row>

 {/* DETAILS MODAL */}
 <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
 <Modal.Header closeButton>
 <Modal.Title>Invoice Details</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedBilling && (
 <>
 <Row>
 <Col md={6}><strong>Invoice:</strong> {selectedBilling.invoiceNumber}</Col>
 <Col md={6}><strong>Status:</strong> <Badge className="rounded-pill ms-2" bg={getStatusBadge(selectedBilling.status)}>{selectedBilling.status}</Badge></Col>
 </Row>

 <Row className="mt-2">
 <Col md={6}><strong>Total:</strong> {formatCurrency(selectedBilling.totalAmount)}</Col>
 <Col md={6}><strong>Due:</strong> {formatCurrency(selectedBilling.amountDue)}</Col>
 </Row>

 <hr />

 {selectedBilling.lineItems?.length > 0 && (
 <>
 <h6>Line Items</h6>
 <Table bordered size="sm">
 <thead>
 <tr>
 <th>#</th>
 <th>Description</th>
 <th>Qty</th>
 <th>Unit</th>
 <th>Rate</th>
 <th>Amount</th>
 </tr>
 </thead>
 <tbody>
 {selectedBilling.lineItems.map((it, idx) => (
 <tr key={idx}>
 <td>{idx + 1}</td>
 <td>{it.description}</td>
 <td>{it.quantity}</td>
 <td>{it.unit}</td>
 <td>{formatCurrency(it.rate)}</td>
 <td>{formatCurrency(it.amount)}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </>
 )}
 </>
 )}
 </Modal.Body>
 <Modal.Footer>
 {selectedBilling && (
 <Button variant="primary" onClick={() => handlePreviewPDF(selectedBilling._id)}>
 Preview PDF
 </Button>
 )}
 <Button variant="secondary" onClick={() => setShowModal(false)}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>

 {/* PDF LOADER */}
 {(previewingPdf || downloadingPdf) && (
 <div
 className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
 style={{ background: "rgba(0,0,0,0.4)", zIndex: 9999 }}
 >
 <div className="bg-white p-4 rounded shadow text-center">
 <div className="spinner-border text-primary mb-3"></div>
 <h6 className="mb-0">
 {previewingPdf ? "Preparing Invoice PDF Preview…" : "Generating Invoice PDF for Download…"}
 </h6>
 <p className="small text-muted mt-2 mb-0">This may take a few minutes for large documents.</p>
 </div>
 </div>
 )}
 </>
 );
};

export default BillingTab;
